import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";

const DispatchPlan = () => {
    const [loading, setLoading] = useState(false);
    const [dispatchData, setDispatchData] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const perPage = 10;

    // =========================
    // FETCH DISPATCH HISTORY
    // =========================
    const fetchDispatchHistory = async () => {
        const token = getAuthToken();

        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        try {
            setLoading(true);

            const response = await axios.post(
                `${config.API}/senmanager/dispatchhistory`,
                {}, // POST body
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.success) {
                setDispatchData(
                    response.data?.data || []
                );
            } else {
                setDispatchData([]);

                toast.warning(
                    response.data?.message ||
                    "No dispatch data found."
                );
            }
        } catch (error) {
            console.error(
                "Dispatch History Error:",
                error
            );

            setDispatchData([]);

            toast.error(
                error.response?.data?.message ||
                "Failed to load dispatch history."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDispatchHistory();
    }, []);

    // =========================
    // SEARCH
    // =========================
    const filteredData = useMemo(() => {
        const value = search
            .trim()
            .toLowerCase();

        if (!value) {
            return dispatchData;
        }

        return dispatchData.filter((item) =>
            [
                item.poNumber,
                item.invNo,
                item.productCode,
                item.productName,
            ].some((field) =>
                String(field || "")
                    .toLowerCase()
                    .includes(value)
            )
        );
    }, [dispatchData, search]);

    // Reset pagination after search
    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    // =========================
    // PAGINATION
    // =========================
    const totalPages = Math.ceil(
        filteredData.length / perPage
    );

    const paginatedData = useMemo(() => {
        const start =
            (currentPage - 1) * perPage;

        return filteredData.slice(
            start,
            start + perPage
        );
    }, [filteredData, currentPage]);

    // =========================
    // UNIQUE PO COUNT
    // =========================
    const uniquePOs = useMemo(() => {
        return new Set(
            filteredData
                .map((item) => item.poNumber)
                .filter(Boolean)
        ).size;
    }, [filteredData]);

    // =========================
    // TOTALS
    // =========================
    const totals = useMemo(() => {
        return filteredData.reduce(
            (acc, item) => {
                acc.ordered += Number(
                    item.orderedQty || 0
                );

                acc.dispatch += Number(
                    item.dispatchQty || 0
                );

                acc.pending += Number(
                    item.pendingQty || 0
                );

                return acc;
            },
            {
                ordered: 0,
                dispatch: 0,
                pending: 0,
            }
        );
    }, [filteredData]);

    // =========================
    // FORMAT NUMBER
    // =========================
    const formatNumber = (value) => {
        return Number(value || 0).toLocaleString(
            "en-IN"
        );
    };

    // =========================
    // FORMAT DATE
    // =========================
    const formatDate = (date) => {
        if (!date) {
            return "-";
        }

        const parsedDate = new Date(date);

        if (isNaN(parsedDate.getTime())) {
            return "-";
        }

        return parsedDate.toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        );
    };

    // =========================
    // DISPATCH %
    // =========================
    const getDispatchPercentage = (item) => {
        const ordered = Number(
            item.orderedQty || 0
        );

        const dispatched = Number(
            item.dispatchQty || 0
        );

        if (ordered <= 0) {
            return 0;
        }

        return Math.min(
            100,
            Math.max(
                0,
                (dispatched / ordered) * 100
            )
        );
    };

    return (
        <div className="container-fluid py-4">

            {/* ================= HEADER ================= */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">

                <div>
                    <div className="d-flex align-items-center gap-2 mb-1">

                        <h4 className="fw-bold mb-0">
                            Dispatch History
                        </h4>

                        <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle">
                            ERP
                        </span>

                    </div>

                    <div className="text-muted small">
                        Dispatch history and order
                        fulfillment overview
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2"
                    onClick={fetchDispatchHistory}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                            />

                            Loading...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-arrow-clockwise" />

                            Refresh
                        </>
                    )}
                </button>

            </div>

            {/* ================= SUMMARY ================= */}
            <div className="row g-3 mb-4">

                {/* UNIQUE POS */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between align-items-start">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Unique PO
                                    </div>

                                    <div className="fs-4 fw-bold">
                                        {uniquePOs.toLocaleString(
                                            "en-IN"
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-primary-subtle text-primary p-2">
                                    <i className="bi bi-file-earmark-text fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

                {/* DISPATCH RECORDS */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between align-items-start">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Dispatch Records
                                    </div>

                                    <div className="fs-4 fw-bold">
                                        {filteredData.length.toLocaleString(
                                            "en-IN"
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-info-subtle text-info p-2">
                                    <i className="bi bi-receipt fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

                {/* DISPATCHED */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between align-items-start">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Total Dispatched
                                    </div>

                                    <div className="fs-4 fw-bold text-success">
                                        {formatNumber(
                                            totals.dispatch
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-success-subtle text-success p-2">
                                    <i className="bi bi-truck fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

                {/* PENDING */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between align-items-start">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Total Pending
                                    </div>

                                    <div className="fs-4 fw-bold text-warning">
                                        {formatNumber(
                                            totals.pending
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-warning-subtle text-warning p-2">
                                    <i className="bi bi-clock-history fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

            </div>

            {/* ================= TABLE ================= */}
            <div className="card border-0 shadow-sm">

                {/* TOOLBAR */}
                <div className="card-header bg-white border-0 p-3 p-lg-4">

                    <div className="row align-items-center g-3">

                        <div className="col-12 col-lg-5">

                            <h6 className="fw-bold mb-1">
                                Dispatch History
                            </h6>

                            <small className="text-muted">
                                {filteredData.length} records
                            </small>

                        </div>

                        <div className="col-12 col-lg-7">

                            <div className="d-flex justify-content-lg-end">

                                <div
                                    className="input-group"
                                    style={{
                                        maxWidth:
                                            "420px",
                                    }}
                                >

                                    <span className="input-group-text bg-white">
                                        <i className="bi bi-search" />
                                    </span>

                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search PO, invoice, product..."
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(
                                                e.target.value
                                            )
                                        }
                                    />

                                    {search && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() =>
                                                setSearch("")
                                            }
                                        >
                                            <i className="bi bi-x" />
                                        </button>
                                    )}

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

                {/* TABLE */}
                <div className="card-body p-0">

                    {loading ? (

                        <div className="py-5 text-center">

                            <div
                                className="spinner-border text-primary"
                                role="status"
                            />

                            <div className="mt-3 text-muted">
                                Loading dispatch history...
                            </div>

                        </div>

                    ) : (

                        <div className="table-responsive">

                            <table className="table table-hover align-middle mb-0">

                                <thead className="table-light">

                                    <tr>

                                        <th className="px-4">
                                            #
                                        </th>

                                        <th>
                                            DISPATCH DATE
                                        </th>

                                        <th>
                                            PO NUMBER
                                        </th>

                                        <th>
                                            INVOICE
                                        </th>

                                        <th>
                                            PRODUCT
                                        </th>

                                        <th className="text-end">
                                            ORDERED
                                        </th>

                                        <th className="text-end">
                                            DISPATCHED
                                        </th>

                                        <th className="text-end">
                                            PENDING
                                        </th>

                                        <th className="text-end">
                                            RUNNING
                                        </th>

                                        <th>
                                            PROGRESS
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {paginatedData.length > 0 ? (

                                        paginatedData.map(
                                            (item, index) => {

                                                const progress =
                                                    getDispatchPercentage(
                                                        item
                                                    );

                                                return (
                                                    <tr
                                                        key={
                                                            item.saleRecId ||
                                                            index
                                                        }
                                                    >

                                                        {/* NUMBER */}
                                                        <td className="px-4 text-muted fw-semibold">
                                                            {(currentPage -
                                                                1) *
                                                                perPage +
                                                                index +
                                                                1}
                                                        </td>

                                                        {/* DATE */}
                                                        <td>
                                                            <div className="fw-semibold text-nowrap">
                                                                {formatDate(
                                                                    item.dispatchDate
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* PO */}
                                                        <td>
                                                            <span className="badge bg-light text-dark border fw-semibold">
                                                                {item.poNumber ||
                                                                    "-"}
                                                            </span>
                                                        </td>

                                                        {/* INVOICE */}
                                                        <td>
                                                            <span className="text-muted text-nowrap">
                                                                {item.invNo ||
                                                                    "-"}
                                                            </span>
                                                        </td>

                                                        {/* PRODUCT */}
                                                        <td
                                                            style={{
                                                                minWidth:
                                                                    "320px",
                                                                maxWidth:
                                                                    "450px",
                                                            }}
                                                        >

                                                            <div className="fw-semibold text-dark">
                                                                {item.productName ||
                                                                    "-"}
                                                            </div>

                                                            <small className="text-muted">
                                                                {item.productCode ||
                                                                    "-"}
                                                            </small>

                                                        </td>

                                                        {/* ORDERED */}
                                                        <td className="text-end fw-semibold">
                                                            {formatNumber(
                                                                item.orderedQty
                                                            )}
                                                        </td>

                                                        {/* DISPATCHED */}
                                                        <td className="text-end">

                                                            <span className="fw-bold text-success">
                                                                {formatNumber(
                                                                    item.dispatchQty
                                                                )}
                                                            </span>

                                                        </td>

                                                        {/* PENDING */}
                                                        <td className="text-end">

                                                            <span
                                                                className={
                                                                    `fw-bold ${Number(
                                                                        item.pendingQty ||
                                                                        0
                                                                    ) >
                                                                        0
                                                                        ? "text-warning"
                                                                        : "text-success"
                                                                    }`
                                                                }
                                                            >
                                                                {formatNumber(
                                                                    item.pendingQty
                                                                )}
                                                            </span>

                                                        </td>

                                                        {/* RUNNING */}
                                                        <td className="text-end">

                                                            <span className="fw-semibold text-primary">
                                                                {formatNumber(
                                                                    item.runningDispatch
                                                                )}
                                                            </span>

                                                        </td>

                                                        {/* PROGRESS */}
                                                        <td
                                                            style={{
                                                                minWidth:
                                                                    "170px",
                                                            }}
                                                        >

                                                            <div className="d-flex align-items-center gap-2">

                                                                <div
                                                                    className="progress flex-grow-1"
                                                                    style={{
                                                                        height:
                                                                            "7px",
                                                                    }}
                                                                >

                                                                    <div
                                                                        className="progress-bar bg-success"
                                                                        role="progressbar"
                                                                        style={{
                                                                            width: `${progress}%`,
                                                                        }}
                                                                    />

                                                                </div>

                                                                <small className="fw-semibold text-muted">
                                                                    {progress.toFixed(
                                                                        0
                                                                    )}
                                                                    %
                                                                </small>

                                                            </div>

                                                        </td>

                                                    </tr>
                                                );
                                            }
                                        )

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan="10"
                                                className="text-center py-5"
                                            >

                                                <div className="text-muted mb-2">
                                                    <i className="bi bi-truck fs-2" />
                                                </div>

                                                <div className="fw-semibold">
                                                    No dispatch records
                                                    found
                                                </div>

                                                <small className="text-muted">
                                                    Try changing your
                                                    search.
                                                </small>

                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                                {/* TOTAL */}
                                {filteredData.length > 0 && (
                                    <tfoot className="table-light">

                                        <tr>

                                            <th
                                                colSpan="5"
                                                className="px-4 text-end"
                                            >
                                                TOTAL
                                            </th>

                                            <th className="text-end">
                                                {formatNumber(
                                                    totals.ordered
                                                )}
                                            </th>

                                            <th className="text-end text-success">
                                                {formatNumber(
                                                    totals.dispatch
                                                )}
                                            </th>

                                            <th className="text-end text-warning">
                                                {formatNumber(
                                                    totals.pending
                                                )}
                                            </th>

                                            <th className="text-end text-primary">
                                                —
                                            </th>

                                            <th />

                                        </tr>

                                    </tfoot>
                                )}

                            </table>

                        </div>

                    )}

                </div>

                {/* ================= PAGINATION ================= */}
                {!loading && filteredData.length > 0 && totalPages > 1 && (
                    <div className="border-top px-3 py-3">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">

                            {/* RECORD INFO */}
                            <div className="text-muted small text-nowrap">
                                Showing{" "}
                                <strong>
                                    {(currentPage - 1) * perPage + 1}
                                </strong>
                                {" - "}
                                <strong>
                                    {Math.min(
                                        currentPage * perPage,
                                        filteredData.length
                                    )}
                                </strong>
                                {" of "}
                                <strong>{filteredData.length}</strong>
                            </div>

                            {/* PAGINATION */}
                            <nav>
                                <ul className="pagination pagination-sm mb-0 flex-nowrap">

                                    {/* PREVIOUS */}
                                    <li
                                        className={`page-item ${currentPage === 1
                                                ? "disabled"
                                                : ""
                                            }`}
                                    >
                                        <button
                                            type="button"
                                            className="page-link"
                                            disabled={currentPage === 1}
                                            onClick={() =>
                                                setCurrentPage(
                                                    (page) => page - 1
                                                )
                                            }
                                        >
                                            <i className="bi bi-chevron-left" />
                                            <span className="d-none d-md-inline ms-1">
                                                Previous
                                            </span>
                                        </button>
                                    </li>

                                    {/* FIRST PAGE */}
                                    <li
                                        className={`page-item ${currentPage === 1
                                                ? "active"
                                                : ""
                                            }`}
                                    >
                                        <button
                                            type="button"
                                            className="page-link"
                                            onClick={() =>
                                                setCurrentPage(1)
                                            }
                                        >
                                            1
                                        </button>
                                    </li>

                                    {/* LEFT ELLIPSIS */}
                                    {currentPage > 3 && (
                                        <li className="page-item disabled">
                                            <span className="page-link">
                                                ...
                                            </span>
                                        </li>
                                    )}

                                    {/* MIDDLE PAGES */}
                                    {Array.from(
                                        { length: 3 },
                                        (_, index) =>
                                            currentPage - 1 + index
                                    )
                                        .filter(
                                            (page) =>
                                                page > 1 &&
                                                page < totalPages
                                        )
                                        .map((page) => (
                                            <li
                                                key={page}
                                                className={`page-item ${currentPage === page
                                                        ? "active"
                                                        : ""
                                                    }`}
                                            >
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    onClick={() =>
                                                        setCurrentPage(
                                                            page
                                                        )
                                                    }
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        ))}

                                    {/* RIGHT ELLIPSIS */}
                                    {currentPage < totalPages - 2 && (
                                        <li className="page-item disabled">
                                            <span className="page-link">
                                                ...
                                            </span>
                                        </li>
                                    )}

                                    {/* LAST PAGE */}
                                    {totalPages > 1 && (
                                        <li
                                            className={`page-item ${currentPage ===
                                                    totalPages
                                                    ? "active"
                                                    : ""
                                                }`}
                                        >
                                            <button
                                                type="button"
                                                className="page-link"
                                                onClick={() =>
                                                    setCurrentPage(
                                                        totalPages
                                                    )
                                                }
                                            >
                                                {totalPages}
                                            </button>
                                        </li>
                                    )}

                                    {/* NEXT */}
                                    <li
                                        className={`page-item ${currentPage === totalPages
                                                ? "disabled"
                                                : ""
                                            }`}
                                    >
                                        <button
                                            type="button"
                                            className="page-link"
                                            disabled={
                                                currentPage === totalPages
                                            }
                                            onClick={() =>
                                                setCurrentPage(
                                                    (page) => page + 1
                                                )
                                            }
                                        >
                                            <span className="d-none d-md-inline me-1">
                                                Next
                                            </span>
                                            <i className="bi bi-chevron-right" />
                                        </button>
                                    </li>

                                </ul>
                            </nav>
                        </div>
                    </div>
                )}

            </div>

        </div>
    );
};

export default DispatchPlan;